/**
 * Customer Segment Export Utilities
 * Handles CSV, Excel and Mailchimp export for customer segments
 */
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

/**
 * Default fields included in an export, in order.
 */
const DEFAULT_FIELDS = [
  { key: "email", label: "E-mailadres" },
  { key: "firstName", label: "Voornaam" },
  { key: "lastName", label: "Achternaam" },
  { key: "phoneNumber", label: "Telefoonnummer" },
  { key: "segmentName", label: "Segment" },
  { key: "shopSource", label: "Shop" },
  // { key: "lastOrderDate", label: "Laatste bestelling" },
];

/**
 * Convert customer data to CSV format
 * @param {Array} customers - Array of customer objects
 * @param {Array} fields - Fields to include in export
 * @returns {string} CSV string
 */
export const customersToCSV = (customers, fields = null) => {
  if (!customers || customers.length === 0) {
    return "";
  }

  const exportFields = fields || DEFAULT_FIELDS;

  // Create CSV header
  const header = exportFields.map((f) => f.label).join(",");

  // Create CSV rows
  const rows = customers.map((customer) => {
    return exportFields
      .map((field) => {
        const value = customer[field.key];

        if (value === null || value === undefined) {
          return "";
        }

        if (typeof value === "number") {
          return value;
        }

        // Escape commas and quotes in strings
        const stringValue = String(value);
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      })
      .join(",");
  });

  return [header, ...rows].join("\n");
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = "export.csv") => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

const buildExportFilename = (segmentName, extension) => {
  const timestamp = new Date().toISOString().split("T")[0];
  return `${(segmentName || "customers").toLowerCase().replace(/\s+/g, "_")}_${timestamp}.${extension}`;
};

/**
 * Export customers to CSV and download
 * @param {Array} customers - Array of customer objects
 * @param {string} segmentName - Name of segment for filename
 * @param {Array} fields - Optional custom fields
 */
export const exportCustomersToCSV = (customers, segmentName = "customers", fields = null) => {
  const csv = customersToCSV(customers, fields);
  downloadCSV(csv, buildExportFilename(segmentName, "csv"));
};

/**
 * A worksheet tab name may not exceed 31 characters and may not contain : \ / ? * [ ]
 */
const sanitizeSheetName = (name) => {
  const cleaned = (name || "Segment").replace(/[:\\/?*[\]]/g, "").trim();
  return cleaned.slice(0, 31) || "Segment";
};

/**
 * Build an Excel workbook for the given customers, grouped into one sheet per segment
 * @param {Array} customers - Array of customer objects (each with segmentName)
 * @returns {XLSX.WorkBook} workbook
 */
export const buildSegmentExcelWorkbook = (customers) => {
  const workbook = XLSX.utils.book_new();

  const groups = customers.reduce((acc, customer) => {
    const key = customer.segmentName || "Onbekend";
    if (!acc[key]) acc[key] = [];
    acc[key].push(customer);
    return acc;
  }, {});

  const headers = DEFAULT_FIELDS.filter((f) => f.key !== "segmentName").map((f) => f.label);
  const rowFields = DEFAULT_FIELDS.filter((f) => f.key !== "segmentName");

  const usedNames = new Set();
  Object.entries(groups).forEach(([segmentName, group]) => {
    let sheetName = sanitizeSheetName(segmentName);
    let suffix = 2;
    while (usedNames.has(sheetName)) {
      sheetName = `${sanitizeSheetName(segmentName).slice(0, 28)} (${suffix++})`;
    }
    usedNames.add(sheetName);

    const rows = group.map((customer) => rowFields.map((field) => customer[field.key] || ""));
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  return workbook;
};

/**
 * Export customers to a clear, per-segment Excel file and download it
 * @param {Array} customers - Array of customer objects
 * @param {string} segmentName - Name of segment for the filename
 */
export const exportCustomersToExcel = (customers, segmentName = "customers") => {
  const workbook = buildSegmentExcelWorkbook(customers);
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, buildExportFilename(segmentName, "xlsx"));
};

/**
 * Format customers for Mailchimp import
 * Mailchimp requires specific format: Email Address, First Name, Last Name, Tags
 * @param {Array} customers - Array of customer objects
 * @param {string} segmentTag - Tag to apply to all customers
 * @returns {string} Mailchimp-formatted CSV
 */
export const customersToMailchimpCSV = (customers, segmentTag = "") => {
  if (!customers || customers.length === 0) {
    return "";
  }

  // Mailchimp CSV format
  const header = "Email Address,First Name,Last Name,Tags";

  const rows = customers.map((customer) => {
    const email = customer.email || "";
    const firstName = customer.firstName || "";
    const lastName = customer.lastName || "";

    // Build tags
    const tags = [];
    if (segmentTag) tags.push(segmentTag);
    if (customer.segmentName) tags.push(customer.segmentName);
    if (customer.shopSource) tags.push(customer.shopSource);

    const tagsString = tags.join(";");

    // Escape fields if needed
    const escapeField = (field) => {
      if (field.includes(",") || field.includes('"')) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    return [escapeField(email), escapeField(firstName), escapeField(lastName), escapeField(tagsString)].join(",");
  });

  return [header, ...rows].join("\n");
};

/**
 * Export customers for Mailchimp and download
 * @param {Array} customers - Array of customers
 * @param {string} segmentName - Name of segment
 */
export const exportCustomersToMailchimp = (customers, segmentName = "segment") => {
  const csv = customersToMailchimpCSV(customers, segmentName);
  downloadCSV(csv, `mailchimp_${buildExportFilename(segmentName, "csv")}`);
};

/**
 * Build segment query based on filters
 * @param {Object} filters - Filter object
 * @returns {Object} Query configuration
 */
export const buildSegmentQuery = (filters) => {
  return {
    segment: filters.segment || null,
    shopSource: filters.shopSource || "all",
  };
};

/**
 * Filter customers based on query
 * @param {Array} customers - Array of all customers
 * @param {Object} query - Query object from buildSegmentQuery
 * @returns {Array} Filtered customers
 */
export const filterCustomers = (customers, query) => {
  return customers.filter((customer) => {
    if (query.segment && customer.segmentName !== query.segment) {
      return false;
    }

    if (query.shopSource !== "all" && customer.shopSource !== query.shopSource) {
      return false;
    }

    return true;
  });
};

/**
 * Get export statistics
 * @param {Array} customers - Filtered customers to export
 * @returns {Object} Statistics object
 */
export const getExportStats = (customers) => {
  const stats = {
    totalCustomers: customers.length,
    shopBreakdown: {},
    segmentBreakdown: {},
  };

  customers.forEach((customer) => {
    const shop = customer.shopSource || "Unknown";
    stats.shopBreakdown[shop] = (stats.shopBreakdown[shop] || 0) + 1;

    const segment = customer.segmentName || "Unknown";
    stats.segmentBreakdown[segment] = (stats.segmentBreakdown[segment] || 0) + 1;
  });

  return stats;
};

/**
 * Validate email for export
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Clean customer data for export
 * Removes customers with invalid emails and duplicates
 * @param {Array} customers - Array of customers
 * @returns {Array} Cleaned customers
 */
export const cleanCustomersForExport = (customers) => {
  const seen = new Set();

  return customers.filter((customer) => {
    // Must have valid email
    if (!isValidEmail(customer.email)) {
      return false;
    }

    // Remove duplicates
    if (seen.has(customer.email.toLowerCase())) {
      return false;
    }

    seen.add(customer.email.toLowerCase());
    return true;
  });
};
